import { useCallback, useEffect, useRef, useState } from 'react';
import {
  grenzpaketStatus,
  grenzpaketSync,
  grenzpaketLogin,
  grenzpaketLog,
  isGrenzpaketOn,
  setGrenzpaketOn,
  getDefaultGrenzpaketType,
  setDefaultGrenzpaketType,
  isDemoModeActive,
  setDemoModeActive,
  gt,
} from '../services/grenzpaket';

/**
 * 🇨🇭 Einstellungen → Grenzpaket.ch-Anbindung (Zugangsdaten, automatischer Hintergrund-Sync & Modul-Setup).
 *
 * Der Paketbestand wird vollautomatisch im Hintergrund mit shop.grenzpaket.ch abgeglichen
 * (kein manueller Rapport-Import nötig). Dieses Fenster bündelt nur die Grenzpaket-Zugangsdaten,
 * den Sync-Status und die modulspezifischen Einstellungen, damit das normale PaketPilot-Setup sauber bleibt.
 */
export default function GrenzpaketPanel({ colors, language = 'de', onToggle, onClose }) {
  const T = gt(language);
  const dark = !!colors?.darkMode;
  const f = {
    text: dark ? '#fff' : '#23272f',
    sub: dark ? '#aaa' : '#666',
    rand: dark ? '#444' : '#ddd',
    feld: dark ? '#1b1f27' : '#fff',
    zeile: dark ? '#2d333b' : '#f5f6f8',
  };

  const [status, setStatus] = useState(null);
  const [syncLaedt, setSyncLaedt] = useState(false);
  const [syncMeldung, setSyncMeldung] = useState('');
  const [zeigeLoginFelder, setZeigeLoginFelder] = useState(false);
  const [email, setEmail] = useState('');
  const [passwort, setPasswort] = useState('');
  const [meldetAn, setMeldetAn] = useState(false);
  const [loginMeldung, setLoginMeldung] = useState(null);
  const [an, setAn] = useState(() => isGrenzpaketOn());
  const [defaultTyp, setDefaultTyp] = useState(() => getDefaultGrenzpaketType());
  const [demoMode, setDemoModeState] = useState(() => isDemoModeActive());
  const [log, setLog] = useState(null);

  useEffect(() => {
    const syncDemo = () => setDemoModeState(isDemoModeActive());
    window.addEventListener('kiosk:demo-mode-changed', syncDemo);
    return () => window.removeEventListener('kiosk:demo-mode-changed', syncDemo);
  }, []);

  const lebt = useRef(true);
  useEffect(() => () => { lebt.current = false; }, []);

  const ladeStatus = useCallback(async () => {
    setStatus(null);
    const s = await grenzpaketStatus();
    if (!lebt.current) return;
    setStatus(s);
  }, []);

  const ladeLog = useCallback(async () => {
    const r = await grenzpaketLog(60);
    if (lebt.current) setLog(r.entries || []);
  }, []);

  useEffect(() => {
    ladeStatus();
    ladeLog();
  }, [ladeStatus, ladeLog]);

  const handleJetztSynchronisieren = async () => {
    if (syncLaedt) return;
    setSyncLaedt(true);
    setSyncMeldung('');
    try {
      const r = await grenzpaketSync();
      if (!lebt.current) return;
      if (r && r.ok) {
        const neu = Number(r.neuAngelegt || 0);
        const regal = Number(r.regalAktualisiert || 0);
        const ausgebucht = Number(r.autoAusgebucht || 0);
        const gesamt = Number(r.rapportOffen ?? status?.bestandAnzahl ?? 0);
        setSyncMeldung(
          `✅ Abgleich erfolgreich (${gesamt} offen bei Grenzpaket · +${neu} neu · ${regal}× Regal aktualisiert · ${ausgebucht}× als abgeholt übernommen)`
        );
        await ladeStatus();
        await ladeLog();
      } else {
        setSyncMeldung(`❌ ${r?.message || 'Abgleich fehlgeschlagen'}`);
      }
    } catch (err) {
      if (lebt.current) setSyncMeldung(`❌ ${err?.message || 'Abgleich fehlgeschlagen'}`);
    } finally {
      if (lebt.current) setSyncLaedt(false);
    }
  };

  const anmelden = async () => {
    setMeldetAn(true);
    setLoginMeldung(null);
    const r = await grenzpaketLogin(email.trim(), passwort);
    if (!lebt.current) return;
    setMeldetAn(false);
    setPasswort('');
    setLoginMeldung(
      r.ok
        ? { ok: true, text: T('loginOk') }
        : { ok: false, text: `${T('loginFehler')}${r.message ? ': ' + r.message : ''}` }
    );
    if (r.ok) setZeigeLoginFelder(false);
    ladeStatus();
    ladeLog();
  };

  const schalten = (on) => {
    setGrenzpaketOn(on);
    setAn(on);
    if (onToggle) onToggle();
  };

  const handleDefaultTyp = (typ) => {
    setDefaultGrenzpaketType(typ);
    setDefaultTyp(typ);
  };

  const statusText = () => {
    if (!status) return T('laedt');
    if (status.reason === 'nicht_freigeschaltet') return '⛔ ' + T('stGesperrt');
    if (status.reason === 'offline') return '⚠️ ' + T('stOffline');
    if (!status.angemeldet) return '🟡 ' + T('stLogin') + (status.letzterFehler ? ` (${status.letzterFehler})` : '');
    return `🟢 ${T('stOk')}${status.filiale ? ` · ${T('filiale')}: ${status.filiale}` : ''}${status.emailHinweis ? ` (${status.emailHinweis})` : ''}`;
  };

  const feld = {
    width: '100%', padding: '10px', borderRadius: 8, border: `1px solid ${f.rand}`,
    fontSize: 14, background: f.feld, color: f.text, boxSizing: 'border-box', marginTop: 8,
  };
  const klein = {
    padding: '6px 10px', borderRadius: 8, border: `1px solid ${f.rand}`,
    background: 'transparent', color: f.text, fontSize: 13, cursor: 'pointer',
  };
  const kopf = (txt, rechts) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 18, marginBottom: 6 }}>
      <b style={{ fontSize: 15 }}>{txt}</b>
      {rechts}
    </div>
  );
  const hinweis = (txt) => <div style={{ fontSize: 12.5, color: f.sub, marginTop: 5, lineHeight: 1.45 }}>{txt}</div>;
  const zeit = (iso) => {
    if (!iso) return '–';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '–';
    return `${d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })} ${d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`;
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 7600, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 14 }}>
      <div
        data-testid="grenzpaket-panel"
        style={{
          background: dark ? '#23272f' : '#fff',
          color: f.text,
          borderRadius: 14,
          width: '100%',
          maxWidth: 560,
          maxHeight: '92vh',
          overflowY: 'auto',
          padding: 18,
          boxShadow: '0 10px 40px rgba(0,0,0,0.45)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <b style={{ fontSize: 18 }}>{T('titel')}</b>
          <button type="button" onClick={onClose} style={klein}>{T('schliessen')}</button>
        </div>
        {hinweis('Zugangsdaten und modulspezifische Einstellungen für shop.grenzpaket.ch. Offene Pakete, Regalplätze und Abholungen werden automatisch im Hintergrund synchronisiert.')}

        {/* 1. Verbindungsstatus & Zugangsdaten */}
        {kopf(
          T('status'),
          <div style={{ display: 'flex', gap: 6 }}>
            <button type="button" data-testid="gp-toggle-credentials" onClick={() => setZeigeLoginFelder((v) => !v)} style={klein}>
              🔑 Zugangsdaten
            </button>
            <button type="button" data-testid="gp-status-refresh" onClick={ladeStatus} style={klein}>
              {T('pruefen')}
            </button>
          </div>
        )}
        <div data-testid="gp-status" style={{ padding: '10px 12px', borderRadius: 8, background: f.zeile, fontSize: 13.5, fontWeight: 600 }}>
          {statusText()}
        </div>

        {(zeigeLoginFelder || (status && !status.angemeldet && status.reason !== 'offline')) && (
          <div style={{ marginTop: 10, padding: 12, borderRadius: 10, border: `1px solid ${f.rand}`, background: f.zeile }}>
            <b style={{ fontSize: 14 }}>{T('anmelden')}</b>
            <input
              data-testid="gp-email"
              type="email"
              autoComplete="off"
              placeholder={T('email')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={feld}
            />
            <input
              data-testid="gp-passwort"
              type="password"
              autoComplete="off"
              placeholder={T('passwort')}
              value={passwort}
              onChange={(e) => setPasswort(e.target.value)}
              style={feld}
            />
            <button
              type="button"
              data-testid="gp-login-btn"
              disabled={meldetAn || !email.trim() || !passwort}
              onClick={anmelden}
              style={{
                width: '100%', padding: '11px', borderRadius: 8, marginTop: 10, fontSize: 14, fontWeight: 700,
                border: 'none', background: '#d32f2f', color: '#fff',
                cursor: meldetAn || !email.trim() || !passwort ? 'not-allowed' : 'pointer',
                opacity: meldetAn || !email.trim() || !passwort ? 0.6 : 1,
              }}
            >
              {meldetAn ? T('meldetAn') : T('uebertragen')}
            </button>
          </div>
        )}
        {loginMeldung && (
          <div style={{ marginTop: 8, fontSize: 13.5, fontWeight: 600, color: loginMeldung.ok ? '#2e7d32' : '#c62828' }}>
            {loginMeldung.text}
          </div>
        )}

        {/* 2. Automatischer Hintergrund-Abgleich */}
        {kopf(
          '🔄 Automatischer Bestands-Abgleich (alle 3 Min.)',
          <button
            type="button"
            data-testid="gp-sync-now"
            disabled={syncLaedt}
            onClick={handleJetztSynchronisieren}
            style={{
              ...klein,
              background: '#2563eb',
              color: '#fff',
              border: 'none',
              fontWeight: 700,
              opacity: syncLaedt ? 0.65 : 1,
            }}
          >
            {syncLaedt ? '⏳ Synchronisiert …' : '🔄 Jetzt abgleichen'}
          </button>
        )}
        <div style={{ padding: '10px 12px', borderRadius: 10, background: f.zeile, border: `1px solid ${f.rand}`, fontSize: 13, lineHeight: 1.55 }}>
          <div>
            📦 Synchronisierter Grenzpaket-Bestand:{' '}
            <b>{status?.bestandAnzahl != null ? `${status.bestandAnzahl} offene Pakete` : 'wird geladen …'}</b>
          </div>
          <div style={{ fontSize: 12, color: f.sub }}>
            Letzter automatischer Abgleich: <b>{status?.bestandGelesenAt ? zeit(status.bestandGelesenAt) : '–'}</b>
          </div>
          {syncMeldung && (
            <div style={{ marginTop: 6, fontWeight: 700, color: syncMeldung.startsWith('❌') ? '#c62828' : '#16a34a' }}>
              {syncMeldung}
            </div>
          )}
        </div>

        {/* 3. Grenzpaket-spezifische Setup-Optionen */}
        {kopf('⚙️ Grenzpaket-Setup (nur für dieses Modul)')}
        <div style={{ padding: 12, borderRadius: 10, background: f.zeile, border: `1px solid ${f.rand}` }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
            <input
              data-testid="gp-schalter"
              type="checkbox"
              checked={an}
              onChange={(e) => schalten(e.target.checked)}
              style={{ width: 20, height: 20 }}
            />
            {T('schalter')}
          </label>
          {hinweis(T('schalterHinweis'))}

          <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${f.rand}` }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>
              Standard-Pakettyp bei neuer Annahme:
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {[
                { id: 'Paket', label: '📦 Paket (Standard)' },
                { id: 'PaketKlein', label: '✉️ PaketKlein' },
                { id: 'Brief', label: '📄 Brief' },
              ].map((opt) => {
                const aktiv = defaultTyp === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    data-testid={`gp-default-type-${opt.id}`}
                    onClick={() => handleDefaultTyp(opt.id)}
                    style={{
                      padding: '6px 12px',
                      borderRadius: 8,
                      fontSize: 12.5,
                      fontWeight: 700,
                      cursor: 'pointer',
                      border: `1.5px solid ${aktiv ? '#dc2626' : f.rand}`,
                      background: aktiv ? '#dc2626' : 'transparent',
                      color: aktiv ? '#fff' : f.text,
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* 4. 🧪 A–Z Demo-Buchungsmodus (Trockenlauf mit vollem Prüf-Log, ohne Kunden-E-Mail) */}
        <div
          data-testid="gp-live-locked-box"
          style={{
            marginTop: 16,
            padding: 12,
            borderRadius: 10,
            border: '2px solid #2563eb',
            background: dark ? 'rgba(37, 99, 235, 0.12)' : '#eff6ff',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
            <b style={{ fontSize: 14.5, color: dark ? '#93c5fd' : '#1d4ed8' }}>
              🧪 A–Z Demo-Buchungsmodus (Trockenlauf)
            </b>
            <span style={{
              fontSize: 11, fontWeight: 800, padding: '2px 8px', borderRadius: 999,
              background: dark ? '#065f46' : '#d1fae5', color: dark ? '#a7f3d0' : '#065f46',
            }}>
              AKTIV · KEINE KUNDEN-MAIL
            </span>
          </div>
          <div style={{ fontSize: 12.5, color: f.text, marginTop: 6, lineHeight: 1.45 }}>
            Du kannst den kompletten Ablauf von A bis Z echt durchspielen (inkl. Etikettendruck, Zebra-Lagerscan & Unterschrift).
            Jeder Schritt prüft alle Pflichtfelder und schreibt den exakten Grenzpaket-Datensatz unten ins Prüf-Protokoll —{' '}
            <b>ohne</b> dass <code>shop.grenzpaket.ch</code> eine echte E-Mail/SMS an den Endkunden verschickt:
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10, fontSize: 12.5 }}>
            <div style={{ padding: '6px 9px', borderRadius: 7, background: dark ? 'rgba(0,0,0,0.25)' : '#fff', border: `1px solid ${f.rand}` }}>
              <b>📥 1. Paket-Annahme vorne:</b> Kunde wird abgeglichen (KD-Nr., Bar/Kredit), Paket-Code (<code>B0…</code>/<code>K0…</code>) erzeugt, Grenzpaket-Quittung gedruckt & als <code>demo_annahme</code> geloggt.
            </div>
            <div style={{ padding: '6px 9px', borderRadius: 7, background: dark ? 'rgba(0,0,0,0.25)' : '#fff', border: `1px solid ${f.rand}` }}>
              <b>📍 2. Lager-Scan hinten (Zebra TC75 / Scanner):</b> Einmal Paket-Etikett scannen + einmal Regal-Barcode scannen → setzt Regalplatz (z. B. <code>;P6-C</code>) & loggt <code>demo_lager</code>.
            </div>
            <div style={{ padding: '6px 9px', borderRadius: 7, background: dark ? 'rgba(0,0,0,0.25)' : '#fff', border: `1px solid ${f.rand}` }}>
              <b>📤 3. Paket-Ausgabe vorne:</b> Paket mit Unterschrift ausgeben (zeigt Barzahlungs-/Kredit-Banner) → loggt komplette Ausbuchung als <code>demo_ausgabe</code>.
            </div>
          </div>

          <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${f.rand}`, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <button
              type="button"
              data-testid="gp-demo-mode-toggle"
              onClick={() => {
                const next = !demoMode;
                setDemoModeActive(next);
                setDemoModeState(next);
              }}
              style={{
                padding: '10px 14px',
                borderRadius: 9,
                border: `2px solid ${demoMode ? '#d97706' : '#059669'}`,
                background: demoMode ? '#f59e0b' : '#10b981',
                color: '#fff',
                fontWeight: 800,
                fontSize: 13.5,
                cursor: 'pointer',
              }}
            >
              {demoMode
                ? '⏹️ Visueller Demo-Modus ist AKTIV (echte Pakete ausgeblendet) — Hier beenden'
                : '▶️ Visuellen Demo-Modus einschalten (echte Pakete ausblenden)'}
            </button>
            <div style={{ fontSize: 11.5, color: f.sub, lineHeight: 1.4 }}>
              {demoMode
                ? '🧪 Aktuell sind alle echten Pakete in der Paketliste ausgeblendet — du siehst dort nur deine Demo-Testpakete.'
                : '💡 Wenn eingeschaltet, blendet PaketPilot vorübergehend alle echten Pakete aus, sodass ihr auf einer leeren Test-Bühne nur eure Demo-Pakete seht.'}
            </div>
          </div>
        </div>

        {/* 5. Prüf- & Buchungs-Protokoll */}
        {kopf(
          `🧪 ${T('protokoll')} (A–Z Buchungen & Sync)`,
          <button type="button" onClick={ladeLog} style={klein}>{T('neuLaden')}</button>
        )}
        <div style={{ maxHeight: 280, overflowY: 'auto', borderRadius: 8, background: f.zeile, padding: 8, fontSize: 12.5 }}>
          {!log || log.length === 0 ? (
            <div style={{ color: f.sub, padding: 6 }}>{T('protokollLeer')}</div>
          ) : (
            log.map((e) => {
              let det = null;
              if (e.detail) {
                try { det = typeof e.detail === 'string' ? JSON.parse(e.detail) : e.detail; } catch { det = null; }
              }
              const isDemo = e.op === 'demo_annahme' || e.op === 'demo_lager' || e.op === 'demo_ausgabe';
              const opLabel = {
                demo_annahme: '📥 1. DEMO-ANNAHME',
                demo_lager: '📍 2. DEMO-LAGER-SCAN',
                demo_ausgabe: '📤 3. DEMO-AUSGABE',
                kundensuche: '🔍 KUNDENSUCHE',
                auto_sync: '🔄 AUTO-SYNC',
                anmeldung: '🔑 LOGIN',
              }[e.op] || e.op;

              return (
                <div
                  key={e.id}
                  style={{
                    padding: '7px 8px',
                    marginBottom: 6,
                    borderRadius: 7,
                    border: `1px solid ${isDemo ? (e.ok ? '#10b981' : '#f59e0b') : f.rand}`,
                    background: isDemo
                      ? (dark ? 'rgba(16, 185, 129, 0.08)' : '#f0fdf4')
                      : (dark ? '#1b1f27' : '#ffffff'),
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span>
                      {e.ok ? '✅' : '⚠️'} <b>{opLabel}</b> {e.code ? `· ${e.code}` : ''}
                    </span>
                    <span style={{ color: f.sub, whiteSpace: 'nowrap', fontSize: 11.5 }}>
                      {e.ms != null && e.ms > 0 ? `${e.ms}ms · ` : ''}{zeit(e.created_at)}
                    </span>
                  </div>
                  {e.message && (
                    <div style={{ fontSize: 12, color: f.text, marginTop: 3 }}>
                      {e.message}
                    </div>
                  )}
                  {isDemo && det && (
                    <div style={{
                      marginTop: 5,
                      padding: '5px 7px',
                      borderRadius: 6,
                      background: dark ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.9)',
                      border: `1px solid ${f.rand}`,
                      fontSize: 11.5,
                      lineHeight: 1.45,
                    }}>
                      <div>
                        <b>Empfänger:</b> {det.empfaenger || '–'} · <b>KD-Nr.:</b> {det.kundenNr || '–'}
                        {det.tarif ? <> · <b>Tarif:</b> {det.tarif}</> : null}
                        {det.paketTyp ? <> · <b>Typ:</b> {det.paketTyp}</> : null}
                      </div>
                      <div>
                        <b>Paket-Code:</b> <code>{det.paketCode || '–'}</code>
                        {det.tracking && det.tracking !== '–' ? <> · <b>Sendungsnr.:</b> <code>{det.tracking}</code></> : null}
                        {det.regalplatz ? <> · <b>Regal:</b> <code>{det.regalplatz}</code> (Grenzpaket: <code>;{det.regalplatz}</code>)</> : null}
                        {det.vorsortierung ? <> · <b>Vorsortierung:</b> {det.vorsortierung}</> : null}
                        {det.hatUnterschrift !== undefined ? <> · <b>Unterschrift:</b> {det.hatUnterschrift ? '✍️ Vorhanden' : '❌ Fehlt'}</> : null}
                      </div>
                      {Array.isArray(det.fehlendeFelder) && det.fehlendeFelder.length > 0 && (
                        <div style={{ color: '#d97706', fontWeight: 700, marginTop: 2 }}>
                          ⚠️ Fehlende Pflichtfelder: {det.fehlendeFelder.join(', ')}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
